import { inject, Injectable } from '@angular/core';
import { AudioPicker, EntityEditCustomFields, EntityEditForm, EntityEditFormValue, ImagePicker, PdfPicker, RichTextEditor, VideoPicker } from '../models/entity/edit/form';
import { FormControl, FormGroup } from '@angular/forms';
import { requiredValidator } from '../components/validators/required';
import { Subject } from 'rxjs';
import { CustomFieldType } from '../models/entity/custom-field';
import { CanAddCustomFieldResponse, SubscriptionService } from './subscription.service';
import { ToastService } from './toast.service';
import { VideoService } from './video.service';
import { removeItemFromArray } from '../utils/array';
import { DisplayMedia } from '../models/image';
import { FileService } from './file.service';
import { ImageService } from './image.service';
import { SessionService } from './session.service';
import { LoggingService } from './logging.service';
import { SupabaseService } from './supabase.service';

@Injectable({
  providedIn: 'root'
})
export class CustomFieldService {
  subscriptionService = inject(SubscriptionService);
  toastService = inject(ToastService);
  videoService = inject(VideoService);
  fileService = inject(FileService);
  imageService = inject(ImageService);
  sessionService = inject(SessionService);
  loggingService = inject(LoggingService);
  supabaseService = inject(SupabaseService);

  customFieldDeleted = new Subject<number>();

  private constructCustomField(customFieldType?: CustomFieldType | null) {
    const group = new FormGroup<EntityEditCustomFields>({
      fieldType: new FormControl(customFieldType ?? null),
    });
    return group;
  }

  // this method applies the custom field type.
  applyCustomFieldControl(formControl: FormGroup<EntityEditCustomFields>, customFieldType: CustomFieldType) {
    if (customFieldType === CustomFieldType.RichTextEditor) {
      formControl.addControl('richTextEditor', new FormGroup<RichTextEditor | null>({
        title: new FormControl('', [requiredValidator("Rich Text Field Title")]),
        value: new FormControl(''),
      }));
    } else if (customFieldType === CustomFieldType.Images) {
      formControl.addControl('imagePicker', new FormGroup<ImagePicker | null>({
        title: new FormControl('', [requiredValidator("Images Title")]),
        value: new FormControl([]),
      }));
    } else if (customFieldType === CustomFieldType.Videos) {
      formControl.addControl('videoPicker', new FormGroup<VideoPicker | null>({
        title: new FormControl('', [requiredValidator("Videos Title")]),
        value: new FormControl([]),
      }));
    } else if (customFieldType === CustomFieldType.AudioFile) {
      formControl.addControl('audioPicker', new FormGroup<AudioPicker | null>({
        title: new FormControl('', [requiredValidator("Audio Title")]),
        value: new FormControl<DisplayMedia | null>(null),
      }));
    } else if (customFieldType === CustomFieldType.PdfFile) {
      formControl.addControl('pdfPicker', new FormGroup<PdfPicker | null>({
        title: new FormControl('', [requiredValidator("PDF Title")]),
        value: new FormControl<DisplayMedia | null>(null),
      }));
    }
  }

  // adds the overall custom field with possibly no custom field type.
  async add(form: FormGroup<EntityEditForm>, index = form.controls.customFields.length, customFieldType?: CustomFieldType, skipChecks?: boolean) {
    if (!skipChecks) {
      const canAdd = await this.canAddCustomField(form);
      if (!canAdd) return;
    }
    form.controls.customFields.insert(index, this.constructCustomField(customFieldType));
  }

  removeByIndex(form: FormGroup<EntityEditForm>, index: number) {
    const fieldToRemove = form.controls.customFields.at(index);
    const fieldId = fieldToRemove.value.id;
    if (fieldId) {
      this.customFieldDeleted.next(fieldId);
    }
    form.controls.customFields.removeAt(index);
  }

  async canAddCustomField(form: FormGroup<EntityEditForm>) {
    const isStaff = await this.sessionService.isStaffAccountAsync();
    if (isStaff) return true;
    await this.subscriptionService.getSubscription();
    const response = this.subscriptionService.canAddCustomField(form);
    if (!response.canAdd) {
      this.toastService.error(response.reason);
      return false;
    };
    return true;
  }

  async canSelectCustomFieldType(customFieldType: CustomFieldType): Promise<CanAddCustomFieldResponse> {
    const isStaff = await this.sessionService.isStaffAccountAsync();
    if (isStaff) return { canAdd: true };
    return this.subscriptionService.canSelectCustomFieldType(customFieldType);
  }

  async uploadVideos(model: EntityEditFormValue) {
    const customFieldVideos = model.customFields?.filter(c => c.fieldType === CustomFieldType.Videos) || [];
    for (const field of customFieldVideos) {
      let allVideos = field.videoPicker?.value || [];
      const newVideos = allVideos.filter(v => v.status === 'new');
      if (!allVideos.length) {
        removeItemFromArray(customFieldVideos, field);
        continue;
      }
      const videos = newVideos.map(v => v.originalFile).filter(f => f) as File[];

      const largeVideos: File[] = [];
      const smallVideos: File[] = [];
      videos.forEach(video => {
        const bytes = video.size;
        const mb = bytes / (1024 * 1024);
        if (mb > 6) {
          largeVideos.push(video);
        } else {
          smallVideos.push(video);
        }
      });

      const directUploadResponse = await this.videoService.directUpload(smallVideos);
      const resumableUploadResponse = await this.videoService.resumableUpload(largeVideos);
      const combinedResponses = [...directUploadResponse, ...resumableUploadResponse];

      allVideos = allVideos.filter(v => v.status !== 'new');
      allVideos.push(...combinedResponses.map<DisplayMedia>(r => ({ status: 'new', objectName: r.uid })));
      allVideos.forEach(v => {
        delete v.originalFile;
        delete v.src;
        delete v.thumbnail;
      });
      if (field.videoPicker?.value) // null check
        field.videoPicker.value = allVideos;
    }
  }

  async uploadCustomFieldImages(formValue: EntityEditFormValue) {
    const customFields = formValue.customFields?.filter(field => field.fieldType === CustomFieldType.Images) ?? [];
    for (const customField of customFields) {
      let allImages = customField.imagePicker?.value;
      const newImages = customField.imagePicker?.value?.filter(image => image.status === 'new') ?? [];
      if (!allImages?.length) {
        removeItemFromArray(customFields, customField);
        continue;
      }
      for (const newImage of newImages) {
        newImage.originalFile = await this.imageService.compress(newImage.originalFile!, 150);
      }
      const uploadResponses = await Promise.all(newImages.map(i => this.fileService.resumableUpload({ bucketName: 'images', file: i.originalFile! })));
      allImages = allImages.filter(image => image.status !== 'new');
      allImages.push(...uploadResponses.map<DisplayMedia>(u => ({ bucketName: u.bucketName, objectName: u.objectName, status: 'new' })));
      allImages.forEach((image, index) => {
        // we need to delete heavy properties to send a minimal payload on save.
        delete image.originalFile;
        delete image.src;
      });
      if (customField.imagePicker?.value) // null check
        customField.imagePicker.value = allImages;
    }
  }

  deleteUnnecessaryCustomFieldProperties(formValue: EntityEditFormValue) {
    formValue.customFields = formValue.customFields?.map<FormGroup<EntityEditCustomFields>['value']>(f => {
      const filtered: FormGroup<EntityEditCustomFields>['value'] = {};
      const customFieldType = f.fieldType;
      if (customFieldType === CustomFieldType.RichTextEditor) {
        filtered.richTextEditor = f.richTextEditor;
      } else if (customFieldType === CustomFieldType.Images) {
        filtered.imagePicker = f.imagePicker;
      } else if (customFieldType === CustomFieldType.Videos) {
        filtered.videoPicker = f.videoPicker;
      } else if (customFieldType === CustomFieldType.AudioFile) {
        filtered.audioPicker = f.audioPicker;
      }
      filtered.fieldType = customFieldType;
      filtered.id = f.id;
      return filtered;
    });
  }

  async uploadCustomFieldAudio(formValue: EntityEditFormValue): Promise<DisplayMedia | void> {
    const customFields = formValue.customFields?.filter(field => field.fieldType === CustomFieldType.AudioFile) ?? [];

    for (const customField of customFields) {
      const audioFile = customField.audioPicker?.value;

      if (!audioFile || audioFile.status !== 'new' || !audioFile.originalFile) {
        removeItemFromArray(customFields, customField);
        continue;
      }

      try {
        const uploadResponse = await this.fileService.directFileUpload(audioFile.originalFile, 'audio-custom-field', {
          contentType: audioFile.originalFile.type,
          upsert: true
        });

        if (customField.audioPicker?.value) {
          customField.audioPicker.value = {
            bucketName: uploadResponse.bucketName,
            objectName: uploadResponse.objectName,
            status: 'new'
          };
        }
      } catch (error) {
        console.error('Error uploading audio file:', error);
        this.toastService.error('Failed to upload audio file');
      }
    }
  }

  async uploadCustomFieldPdf(formValue: EntityEditFormValue) {
    const fields = formValue.customFields?.filter(c => c.fieldType === CustomFieldType.PdfFile) ?? [];
    try {
      for (const field of fields) {
        const file = field.pdfPicker?.value;
        if (file?.status !== 'new' || !file.originalFile) {
          removeItemFromArray(fields, field);
          continue;
        }
        const objectName = this.fileService.generateObjectName({ bucketName: 'pdf-custom-field', file: file.originalFile });
        const { data, error } = await this.supabaseService.supabase.storage.from('pdf-custom-field').upload(objectName, file.originalFile, { cacheControl: '3600', upsert: false });
        if (error) throw error;

        const uploadResponse: DisplayMedia = {
          status: 'new',
          bucketName: 'pdf-custom-field',
          objectName: data.path,
        };

        field.pdfPicker!.value = uploadResponse;
      }
    }
    catch (e) {
      const error = e as Error;
      this.toastService.error("There was an error saving one or more PDF files");
      this.loggingService.error(error);
    }
  }
}
